/**
 * client/ui/menu.ts
 * Responsabilidad: Gestión del Menú de Inicio, Modal de Alias, Cosméticos y Autenticación con @colyseus/auth.
 */

import type { Room } from "@colyseus/sdk";
import { safeGetStorage, safeSetStorage, sanitizeAlias, generateAnonAlias, clearUserSessionStorage } from "../utils";
import {
    type PlayerCosmeticsConfig,
    type CosmeticCategory,
    type CosmeticCatalogItem,
    type EyeStyle,
    DEFAULT_COSMETICS,
    COSMETIC_CATALOG,
    sanitizeCosmetics,
    drawCosmeticItemThumbnail,
    drawElementalBody,
    drawThrusterPreview,
    getElementalPalette,
} from "../cosmetics";
import { SKIN_IDS } from "../skins";
import { drawModularSkin } from "../skins";
import { getFamiliarImage } from "../renderer";
import { getDefaultSkinForElement } from "../element-skins";
import { drawCanvasElementSkin } from "../element-skins-renderer";
import { tutorialManager } from "../tutorial";
import { markTutorialSeen } from "../onboarding";
import { profileManager } from "../profile";
import { questManager } from "../quests";
import { startTutorialArena } from "../tutorial-arena";
import Swal from "sweetalert2";

export interface MenuResolveResult {
    alias: string;
    startAsSpectator: boolean;
    privateRoomCode?: string;
    privateBotCount?: number;
}

export function parsePlayerCosmetics(skinValue: string): PlayerCosmeticsConfig {
    if (skinValue && skinValue.startsWith("{")) {
        try {
            return sanitizeCosmetics(JSON.parse(skinValue));
        } catch {
            // fallback
        }
    }
    if (skinValue === "eyes") {
        return sanitizeCosmetics({ eyes: "anime", mouth: "smile", glasses: "none", tattoo: "none", aura: "none" });
    }
    if (skinValue === "smile") {
        return sanitizeCosmetics({ eyes: "default", mouth: "smile", glasses: "none", tattoo: "none", aura: "none" });
    }
    return { ...DEFAULT_COSMETICS };
}

const COSMETICS_STORAGE_KEY = "alchem_cosmetics";
const SKIN_STORAGE_KEY = "alchem_skin";

export const PREVIEW_ELEMENTS = [
    { key: "water", label: "💧 Agua" },
    { key: "fire", label: "🔥 Fuego" },
    { key: "plant", label: "🌿 Planta" },
    { key: "electric", label: "⚡ Rayo" },
    { key: "earth", label: "⛰️ Tierra" },
] as const;

export class MenuManager {
    private room: Room | null = null;
    private currentCosmetics: PlayerCosmeticsConfig;
    private currentSkin: string;
    private onAudioActivate?: () => void;

    // Elementos del DOM
    private aliasModal = document.getElementById("alias-modal");
    private aliasInput = document.getElementById("alias-input") as HTMLInputElement | null;
    private aliasSubmit = document.getElementById("alias-confirm-btn") || document.getElementById("alias-submit");
    private aliasSpectateBtn = document.getElementById("spectate-btn") || document.getElementById("spectator-btn") || document.getElementById("alias-spectate-btn");
    private aliasCharCount = document.getElementById("alias-char-count");
    private overlay = document.getElementById("connection-overlay");
    private spawnTooltip = document.getElementById("spawn-tooltip");

    // Cosméticos DOM
    private cosmeticsContainer = document.getElementById("cosmetics-options-container");
    private cosmeticsPreviewCanvas = (document.getElementById("alias-main-preview") ||
        document.getElementById("alias-cosmetics-preview")) as HTMLCanvasElement | null;
    private cosmeticsPreviewCtx = this.cosmeticsPreviewCanvas?.getContext("2d") || null;
    private tabButtons = document.querySelectorAll<HTMLButtonElement>(".cosmetics-tab-btn");

    // Previsualizador Elemental & Catálogo Dinámico
    private previewElementIndex: number = 0;
    private stagePrevBtn = document.getElementById("stage-prev-element");
    private stageNextBtn = document.getElementById("stage-next-element");
    private stageElementName = document.getElementById("stage-element-name");
    private stageSaveBtn = document.getElementById("stage-save-cosmetics-btn");
    private dynamicCatalog: Record<CosmeticCategory, Array<CosmeticCatalogItem>> = COSMETIC_CATALOG;

    // Elementos del Hero Header
    private heroCrystals = document.getElementById("hero-crystals-display");
    private heroOrbs = document.getElementById("hero-orbs-display");
    private heroLoginBtn = document.getElementById("hero-login-btn");
    private heroUserLogged = document.getElementById("hero-user-logged");
    private heroUserName = document.getElementById("hero-user-name");
    private heroLogoutBtn = document.getElementById("hero-logout-btn");
    private heroUserInfoBtn = document.getElementById("hero-user-info-btn");

    constructor(onAudioActivate?: () => void) {
        this.onAudioActivate = onAudioActivate;
        this.currentCosmetics = this.loadSavedCosmetics();
        this.currentSkin = localStorage.getItem(SKIN_STORAGE_KEY) || "default";

        this.initCosmeticsStudio();
        this.initHeroHeaderListeners();
        this.initTutorialButton();
        this.updateHeroHeaderState();

        // Sincronización en tiempo real con questManager (💎 y 🔮)
        questManager.subscribe(() => {
            this.updateHeroHeaderState();
        });

        // Reactividad instantánea tras login (sin recargar página)
        window.addEventListener("alchem:auth_change", (e: any) => {
            const alias = e.detail?.alias;
            if (alias && this.aliasInput) {
                this.aliasInput.value = alias;
            }
            this.updateHeroHeaderState();

            const dbCosmetics = e.detail?.profile?.cosmetics || e.detail?.cosmetics;
            if (dbCosmetics && typeof dbCosmetics === "object" && Object.keys(dbCosmetics).length > 0) {
                this.currentCosmetics = sanitizeCosmetics(dbCosmetics);
                this.saveCosmetics(this.currentCosmetics);
                this.renderStudioPreview();
            }

            // Re-renderizar opciones de cosméticos para actualizar candados de compras
            const activeCat = (document.querySelector(".cosmetics-tab-btn.active") as HTMLElement)?.dataset.category as any || "eyes";
            this.renderCategoryOptions(activeCat);
        });
    }


    public getCosmetics(): PlayerCosmeticsConfig {
        return this.currentCosmetics;
    }

    public getCurrentSkin(): string {
        return this.currentSkin;
    }

    public setRoom(room: Room): void {
        this.room = room;
    }

    /**
     * Muestra el menú de inicio y espera a que el usuario ingrese como jugador o espectador.
     */
    public show(isInstantEntry: boolean = false): Promise<MenuResolveResult> {
        return new Promise((resolve) => {
            if (isInstantEntry) {
                document.body.classList.remove("in-menu");
                if (this.aliasModal) this.aliasModal.classList.add("hidden");
                if (this.spawnTooltip) this.spawnTooltip.classList.add("hidden");
                tutorialManager.skip();
                markTutorialSeen();
                const stored = safeGetStorage("alchem_alias");
                const sanitized = stored ? sanitizeAlias(stored) : "";
                const resolvedAlias = sanitized.length > 0 ? sanitized : generateAnonAlias();
                safeSetStorage("alchem_alias", resolvedAlias);
                resolve({ alias: resolvedAlias, startAsSpectator: false });
                return;
            }

            const stored = safeGetStorage("alchem_alias");
            if (!this.aliasModal || !this.aliasInput || !this.aliasSubmit || !this.aliasCharCount) {
                document.body.classList.remove("in-menu");
                resolve({ alias: stored || generateAnonAlias(), startAsSpectator: false });
                return;
            }

            document.body.classList.add("in-menu");
            this.aliasModal.classList.remove("hidden");
            this.aliasInput.value = stored || "";
            this.currentCosmetics = this.loadSavedCosmetics();
            this.renderStudioPreview();
            this.updateHeroHeaderState();

            const updateCharCount = (): void => {
                if (this.aliasInput && this.aliasCharCount) {
                    const len = this.aliasInput.value.length;
                    this.aliasCharCount.textContent = `${len}/12`;
                }
            };
            this.aliasInput.addEventListener("input", updateCharCount);
            updateCharCount();

            let resolved = false;

            // Elementos de Sala Privada
            let generatedCode = `ALCH-${Math.floor(1000 + Math.random() * 9000)}`;
            let selectedBots = 0;

            const createPrivateRoomBtn = document.getElementById("create-private-room-btn");
            const privateRoomModal = document.getElementById("private-room-modal");
            const privateRoomClose = document.getElementById("private-room-close");
            const privateGeneratedCode = document.getElementById("private-generated-code");
            const privateBotSelector = document.getElementById("private-bot-selector");
            const privateCapacityHint = document.getElementById("private-capacity-hint");
            const privateCopyLinkBtn = document.getElementById("private-copy-link-btn");
            const privateStartHostBtn = document.getElementById("private-start-host-btn");
            const privateJoinCodeInput = document.getElementById("private-join-code-input") as HTMLInputElement | null;
            const privateJoinCodeBtn = document.getElementById("private-join-code-btn");

            const updateBotCapacityHint = (bots: number): void => {
                const maxHumans = 10 - bots;
                if (privateCapacityHint) {
                    privateCapacityHint.textContent = `👥 Cupo: Hasta ${maxHumans} amigos humanos + ${bots} Bots (Máx. 10 en total)`;
                }
            };

            const openPrivateModal = (): void => {
                generatedCode = `ALCH-${Math.floor(1000 + Math.random() * 9000)}`;
                if (privateGeneratedCode) {
                    privateGeneratedCode.textContent = generatedCode;
                }
                selectedBots = 0;
                if (privateBotSelector) {
                    const botBtns = privateBotSelector.querySelectorAll<HTMLButtonElement>(".bot-count-btn");
                    botBtns.forEach((b) => {
                        b.classList.toggle("active", b.getAttribute("data-bots") === "0");
                    });
                }
                updateBotCapacityHint(0);
                if (privateJoinCodeInput) {
                    privateJoinCodeInput.value = "";
                }
                if (privateRoomModal) {
                    privateRoomModal.classList.remove("hidden");
                }
            };

            const closePrivateModal = (): void => {
                if (privateRoomModal) {
                    privateRoomModal.classList.add("hidden");
                }
            };

            if (createPrivateRoomBtn) {
                createPrivateRoomBtn.addEventListener("click", openPrivateModal);
            }
            if (privateRoomClose) {
                privateRoomClose.addEventListener("click", closePrivateModal);
            }
            if (privateRoomModal) {
                privateRoomModal.addEventListener("click", (e) => {
                    if (e.target === privateRoomModal) {
                        closePrivateModal();
                    }
                });
            }

            if (privateBotSelector) {
                const botBtns = privateBotSelector.querySelectorAll<HTMLButtonElement>(".bot-count-btn");
                botBtns.forEach((btn) => {
                    btn.addEventListener("click", () => {
                        const count = Number(btn.getAttribute("data-bots")) || 0;
                        selectedBots = Math.max(0, Math.min(5, count));
                        botBtns.forEach((b) => b.classList.toggle("active", b === btn));
                        updateBotCapacityHint(selectedBots);
                    });
                });
            }

            if (privateCopyLinkBtn) {
                let copyTimer: ReturnType<typeof setTimeout> | null = null;
                privateCopyLinkBtn.addEventListener("click", async () => {
                    const link = `${window.location.origin}${window.location.pathname}?roomId=${generatedCode}`;
                    try {
                        if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
                            await navigator.clipboard.writeText(link);
                        } else {
                            throw new Error("Clipboard API not available");
                        }
                    } catch {
                        const tempInput = document.createElement("input");
                        tempInput.value = link;
                        tempInput.style.position = "fixed";
                        tempInput.style.opacity = "0";
                        document.body.appendChild(tempInput);
                        tempInput.focus();
                        tempInput.select();
                        document.execCommand("copy");
                        document.body.removeChild(tempInput);
                    }
                    privateCopyLinkBtn.textContent = "✅ ¡Enlace Copiado!";
                    if (copyTimer) clearTimeout(copyTimer);
                    copyTimer = setTimeout(() => {
                        privateCopyLinkBtn.textContent = "📋 Copiar Enlace";
                    }, 2000);
                });
            }

            const complete = async (
                startAsSpectator: boolean,
                privateRoomCode?: string,
                privateBotCount?: number
            ): Promise<void> => {
                if (resolved) return;

                if (!startAsSpectator && !privateRoomCode) {
                    const token = safeGetStorage("alchem_auth_token") || safeGetStorage("colyseus-auth-token");
                    if (!token) {
                        const result = await Swal.fire({
                            title: '⚔️ JUGAR COMO INVITADO',
                            html: 'Estás a punto de entrar a la arena como <b>Invitado</b>.<br><br>Si juegas sin registrarte, tu progreso de <b>misiones, logros, cristales ganados y cosméticos NO se guardará permanentemente</b>.<br><br>¿Deseas registrarte gratis ahora o prefieres continuar como invitado?',
                            icon: 'warning',
                            showCancelButton: true,
                            confirmButtonText: '✨ REGISTRARME / INICIAR SESIÓN',
                            cancelButtonText: '⚔️ JUGAR COMO INVITADO',
                            confirmButtonColor: '#38bdf8',
                            cancelButtonColor: '#64748b',
                        });

                        if (result.isConfirmed) {
                            const accountBtn = document.getElementById("open-account-btn") || document.getElementById("alias-account-btn");
                            accountBtn?.click();
                            setTimeout(() => {
                                const regTab = (document.getElementById("auth-mode-register-btn") ||
                                    document.getElementById("account-tab-register") ||
                                    document.querySelector('[data-tab="register"]')) as HTMLElement | null;
                                if (regTab) regTab.click();
                            }, 50);
                            return; // Detener entrada para que el usuario se registre
                        }
                    }
                }

                if (resolved) return;
                resolved = true;

                if (this.onAudioActivate) {
                    this.onAudioActivate();
                }

                const raw = this.aliasInput ? this.aliasInput.value : "";
                const sanitized = sanitizeAlias(raw);
                const finalAlias = sanitized.length > 0 ? sanitized : generateAnonAlias();
                safeSetStorage("alchem_alias", finalAlias);
                if (!privateRoomCode) {
                    void profileManager.syncToDatabase({ alias: finalAlias, cosmetics: this.currentCosmetics });
                }

                if (this.aliasModal) this.aliasModal.classList.add("hidden");
                closePrivateModal();
                document.body.classList.remove("in-menu");

                if (this.aliasInput) this.aliasInput.blur();
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }

                // Transición suave mientras conecta
                if (this.overlay) {
                    this.overlay.classList.remove("hidden");
                }
                await new Promise((r) => setTimeout(r, 250));

                resolve({
                    alias: finalAlias,
                    startAsSpectator,
                    privateRoomCode,
                    privateBotCount,
                });
            };

            if (privateStartHostBtn) {
                privateStartHostBtn.addEventListener("click", () => {
                    closePrivateModal();
                    complete(false, generatedCode, selectedBots);
                });
            }

            const handleJoinCode = (): void => {
                const rawVal = privateJoinCodeInput ? privateJoinCodeInput.value.trim().toUpperCase() : "";
                if (!rawVal) {
                    void Swal.fire({
                        title: "Código Requerido",
                        text: "Por favor introduce el código de la sala privada (ej: 4829 o ALCH-4829).",
                        icon: "warning",
                        confirmButtonText: "Entendido",
                        confirmButtonColor: "#38bdf8",
                        background: "#0f172a",
                        color: "#f8fafc",
                    });
                    return;
                }
                const normalizedCode = rawVal.startsWith("ALCH-") ? rawVal : `ALCH-${rawVal.replace(/^ALCH-?/i, "")}`;
                closePrivateModal();
                complete(false, normalizedCode, 0);
            };

            if (privateJoinCodeBtn) {
                privateJoinCodeBtn.addEventListener("click", handleJoinCode);
            }
            if (privateJoinCodeInput) {
                privateJoinCodeInput.addEventListener("keydown", (e) => {
                    if (e.key === "Enter") {
                        handleJoinCode();
                    }
                });
            }

            this.aliasSubmit.addEventListener("click", () => complete(false));
            if (this.aliasSpectateBtn) {
                this.aliasSpectateBtn.addEventListener("click", () => complete(true));
            }
            this.aliasInput.addEventListener("keydown", (e) => {
                if (e.key === "Enter") complete(false);
            });
            this.aliasInput.focus();
        });
    }

    /**
     * Cicla la skin / cosmético con las teclas 'J' y 'K'.
     */
    public cycleSkin(direction: number): void {
        const idx = SKIN_IDS.indexOf(this.currentSkin as any);
        this.currentSkin = SKIN_IDS[(idx + direction + SKIN_IDS.length) % SKIN_IDS.length];
        localStorage.setItem(SKIN_STORAGE_KEY, this.currentSkin);

        const eyeCatalog = COSMETIC_CATALOG.eyes;
        const eyeIdx = eyeCatalog.findIndex((e) => e.id === this.currentCosmetics.eyes);
        this.currentCosmetics.eyes = (eyeCatalog[(eyeIdx + direction + eyeCatalog.length) % eyeCatalog.length].id as EyeStyle);
        this.saveCosmetics(this.currentCosmetics);

        console.log(`[Cosmetics] Skin: ${this.currentSkin}, Ojos: ${this.currentCosmetics.eyes}`);
        if (this.room) {
            try {
                this.room.send("set-skin", { skin: JSON.stringify(this.currentCosmetics) });
            } catch {
                // ignore
            }
        }
    }

    private updateStageElementDisplay(): void {
        if (this.stageElementName) {
            this.stageElementName.textContent = PREVIEW_ELEMENTS[this.previewElementIndex].label;
        }
    }

    private async fetchCatalog(): Promise<void> {
        try {
            const res = await fetch("/api/shop/cosmetics");
            if (res.ok) {
                const data = await res.json();
                if (data && data.success && data.catalog) {
                    this.dynamicCatalog = data.catalog;
                    const activeCat = (document.querySelector(".cosmetics-tab-btn.active") as HTMLElement)?.dataset.category as CosmeticCategory || "eyes";
                    this.renderCategoryOptions(activeCat);
                }
            }
        } catch (err) {
            console.warn("[Menu] Error al obtener catálogo dinámico de cosméticos:", err);
        }
    }

    public renderCategoryOptions(cat: CosmeticCategory): void {
        const container = this.cosmeticsContainer;
        if (!container) return;
        container.innerHTML = "";
        const items = (this.dynamicCatalog && this.dynamicCatalog[cat]) || COSMETIC_CATALOG[cat] || [];

        items.forEach((item) => {
            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = "cosmetic-option-card cosmetic-card";
            btn.setAttribute("data-item-id", item.id);
            if (item.id === "none" || (cat === "eyes" && item.id === "default")) {
                btn.classList.add("cosmetic-none-card");
            }
            if (this.currentCosmetics[cat] === item.id) {
                btn.classList.add("selected");
            }
            btn.title = `${cat.toUpperCase()}: ${item.label}`;

            const isUnlocked = questManager.hasUnlockedCosmetic(cat, item.id);
            if (!isUnlocked) {
                btn.classList.add("locked");
            }

            const circle = document.createElement("div");
            circle.className = "cosmetic-option-circle";

            const canvas = document.createElement("canvas");
            canvas.width = 56;
            canvas.height = 56;
            canvas.className = "cosmetic-option-canvas";
            const mCtx = canvas.getContext("2d");
            if (mCtx) {
                drawCosmeticItemThumbnail(mCtx, cat, item.id, 56, 56);
            }
            circle.appendChild(canvas);
            btn.appendChild(circle);

            const label = document.createElement("span");
            label.className = "cosmetic-option-label";
            label.textContent = item.label;
            btn.appendChild(label);

            if (isUnlocked || item.price === 0) {
                const freeBadge = document.createElement("span");
                freeBadge.className = "cosmetic-lock-badge free";
                freeBadge.textContent = "✓ Libre";
                btn.appendChild(freeBadge);
            } else {
                const lockBadge = document.createElement("span");
                lockBadge.className = "cosmetic-lock-badge";
                const curIcon = item.currency === "crystals" ? "💎" : "🔮";
                lockBadge.textContent = `🔒 ${item.price} ${curIcon}`;
                btn.appendChild(lockBadge);
            }

            btn.addEventListener("click", async () => {
                const isAlreadySelected = (this.currentCosmetics as any)[cat] === item.id;

                // Si hace clic en el ítem que ya tiene puesto (y no es el default obligatorio de ojos):
                if (isAlreadySelected && item.id !== "default" && item.id !== "none") {
                    // Desequipar
                    (this.currentCosmetics as any)[cat] = "none";
                    this.saveCosmetics(this.currentCosmetics);
                    this.renderStudioPreview();
                    void profileManager.syncToDatabase({ cosmetics: this.currentCosmetics });
                    this.renderCategoryOptions(cat);
                    if (this.room) {
                        try {
                            this.room.send("set-skin", { skin: JSON.stringify(this.currentCosmetics) });
                        } catch {
                            // ignore
                        }
                    }
                    return;
                }

                // Si selecciona 'none' o 'default' (en ojos):
                if (item.id === "none" || (cat === "eyes" && item.id === "default")) {
                    (this.currentCosmetics as any)[cat] = item.id;
                    this.saveCosmetics(this.currentCosmetics);
                    this.renderStudioPreview();
                    void profileManager.syncToDatabase({ cosmetics: this.currentCosmetics });
                    this.renderCategoryOptions(cat);
                    if (this.room) {
                        try {
                            this.room.send("set-skin", { skin: JSON.stringify(this.currentCosmetics) });
                        } catch {
                            // ignore
                        }
                    }
                    return;
                }

                const unlocked = questManager.hasUnlockedCosmetic(cat, item.id);
                if (!unlocked) {
                    const token = safeGetStorage("alchem_auth_token") || safeGetStorage("colyseus-auth-token");
                    if (!token) {
                        await Swal.fire({
                            icon: 'info',
                            title: '¡Crea tu Cuenta de Alquimista!',
                            text: 'Para desbloquear cosméticos permanentes y guardar tu progreso en la nube, inicia sesión o crea una cuenta gratis.',
                            showCancelButton: true,
                            confirmButtonText: 'Iniciar Sesión / Registrarse',
                            cancelButtonText: 'Seguir como Invitado',
                            confirmButtonColor: '#38bdf8',
                            cancelButtonColor: '#64748b',
                        }).then((result) => {
                            if (result.isConfirmed) {
                                const accountBtn = document.getElementById("open-account-btn") || document.getElementById("alias-account-btn") || document.getElementById("account-btn");
                                accountBtn?.click();
                            }
                        });
                        return;
                    }

                    // Previsualización dinámica en memoria usando canvas 100x100
                    const previewConfig = { ...this.currentCosmetics, [cat]: item.id };
                    const pCvs = document.createElement("canvas");
                    pCvs.width = 100;
                    pCvs.height = 100;
                    const pCtx = pCvs.getContext("2d");
                    if (pCtx) {
                        const activeElement = PREVIEW_ELEMENTS[this.previewElementIndex]?.key || "water";
                        if (previewConfig.thruster && previewConfig.thruster !== "none") {
                            drawThrusterPreview(pCtx, 50, 50, 36, previewConfig.thruster, performance.now(), activeElement);
                        }
                        drawElementalBody(pCtx, 50, 50, 36, activeElement, performance.now());
                        drawModularSkin(pCtx, 50, 50, 36, previewConfig, "local", 0, performance.now(), activeElement);
                    }
                    const previewUrl = pCvs.toDataURL();

                    const curIcon = item.currency === "crystals" ? "💎" : "🔮";
                    const result = await Swal.fire({
                        title: `¿DESBLOQUEAR ${item.label.toUpperCase()}?`,
                        imageUrl: previewUrl,
                        imageWidth: 90,
                        imageHeight: 90,
                        imageAlt: item.label,
                        html: `
                            <div style="font-size: 1rem; color: #cbd5e1; margin-top: 8px;">
                                ¿Deseas adquirir este cosmético por <b style="color: #38bdf8; font-size: 1.2rem;">${item.price} ${curIcon}</b>?
                            </div>
                            <div style="font-size: 0.8rem; color: #94a3b8; margin-top: 4px;">
                                (Se equipará automáticamente al confirmar)
                            </div>
                        `,
                        showCancelButton: true,
                        confirmButtonText: `Comprar (${item.price} ${curIcon})`,
                        cancelButtonText: 'Cancelar',
                        confirmButtonColor: '#38bdf8',
                        cancelButtonColor: '#64748b',
                    });

                    if (!result.isConfirmed) return;

                    const curName = item.currency === "crystals" ? "Cristales Alquímicos 💎" : "Orbes Astrales 🔮";
                    const buyRes = await questManager.unlockCosmetic(cat, item.id, item.price, item.currency);
                    if (buyRes.success) {
                        (this.currentCosmetics as any)[cat] = item.id;
                        this.saveCosmetics(this.currentCosmetics);
                        this.renderStudioPreview(); // <-- Redibujado instantáneo garantizado
                        void profileManager.syncToDatabase({ cosmetics: this.currentCosmetics });

                        if (this.room) {
                            try {
                                this.room.send("set-skin", { skin: JSON.stringify(this.currentCosmetics) });
                            } catch {
                                // ignore
                            }
                        }

                        this.renderCategoryOptions(cat);

                        await Swal.fire({
                            icon: 'success',
                            title: '¡Cosmético Desbloqueado!',
                            text: `Has adquirido y equipado ${item.label}.`,
                            timer: 2000,
                            showConfirmButton: false,
                        });
                    } else if (buyRes.requireLogin) {
                        await Swal.fire({
                            icon: 'info',
                            title: 'Inicia Sesión',
                            text: 'Debes iniciar sesión para comprar cosméticos.',
                            confirmButtonText: 'Iniciar Sesión',
                            confirmButtonColor: '#38bdf8',
                        });
                    } else {
                        await Swal.fire({
                            icon: 'warning',
                            title: 'Saldo insuficiente',
                            text: `Saldo insuficiente. Completa misiones diarias o mira anuncios para conseguir más ${curName}.`,
                            confirmButtonText: 'Entendido',
                            confirmButtonColor: '#38bdf8',
                        });
                    }
                    return;
                }

                (this.currentCosmetics as any)[cat] = item.id;
                this.saveCosmetics(this.currentCosmetics);
                this.renderStudioPreview(); // <-- Redibujado instantáneo garantizado
                void profileManager.syncToDatabase({ cosmetics: this.currentCosmetics });

                container.querySelectorAll(".cosmetic-option-card").forEach((el) => {
                    el.classList.remove("selected");
                });
                btn.classList.add("selected");

                if (this.room) {
                    try {
                        this.room.send("set-skin", { skin: JSON.stringify(this.currentCosmetics) });
                    } catch {
                        // ignore
                    }
                }
            });

            container.appendChild(btn);
        });
    }

    public renderStudioPreview(lookAngle: number = 0, t: number = performance.now()): void {
        if (!this.cosmeticsPreviewCtx || !this.cosmeticsPreviewCanvas) return;
        const ctx = this.cosmeticsPreviewCtx;
        const cvs = this.cosmeticsPreviewCanvas;

        ctx.clearRect(0, 0, cvs.width, cvs.height);
        const cx = cvs.width / 2;
        const cy = cvs.height / 2;
        const baseR = 38;
        const pulse = 1 + Math.sin(t * 0.003) * 0.04;

        // Halo alquímico
        const glow = ctx.createRadialGradient(cx, cy, baseR * 0.2, cx, cy, baseR * 1.4);
        glow.addColorStop(0, "rgba(56, 189, 248, 0.45)");
        glow.addColorStop(1, "rgba(0, 0, 0, 0)");
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(cx, cy, baseR * 1.4, 0, Math.PI * 2);
        ctx.fill();

        const activeElement = PREVIEW_ELEMENTS[this.previewElementIndex]?.key || "water";

        // Propulsor alquímico detrás del cuerpo
        drawThrusterPreview(ctx, cx, cy, baseR * pulse, this.currentCosmetics.thruster, t, activeElement);

        // Criatura elemental base como cuerpo principal
        const skinId = (this.currentCosmetics as any)?.elementSkins?.[activeElement] || getDefaultSkinForElement(activeElement);
        const skinDrawn = drawCanvasElementSkin(ctx, cx, cy, baseR * pulse, skinId, activeElement, t, 1.0);
        if (!skinDrawn) {
            // Fallback procedimental mientras carga la imagen
            drawElementalBody(ctx, cx, cy, baseR * pulse, activeElement, t);
        }

        // Capas modulares (Tatuaje, Ojos, Gafas, Boca, Aura cromoadaptativa)
        drawModularSkin(ctx, cx, cy, baseR * pulse, this.currentCosmetics, "local", lookAngle, t, activeElement);

        // Previsualización de Familiar Cosmético (Mascota) a la derecha del pedestal con flotación viva
        const familiarId = this.currentCosmetics.familiar;
        if (familiarId && familiarId !== "none") {
            const famImg = getFamiliarImage(familiarId);
            if (famImg && famImg.complete && famImg.naturalWidth > 0) {
                const bobbing = Math.sin(t * 0.005) * 5;
                const famX = cx + baseR * 1.35;
                const famY = cy - baseR * 0.35 + bobbing;
                const famSize = 42;

                ctx.save();
                // Aura brillante detrás de la mascota
                const famGlow = ctx.createRadialGradient(famX, famY, 4, famX, famY, 26);
                famGlow.addColorStop(0, "rgba(56, 189, 248, 0.5)");
                famGlow.addColorStop(1, "rgba(0, 0, 0, 0)");
                ctx.fillStyle = famGlow;
                ctx.beginPath();
                ctx.arc(famX, famY, 26, 0, Math.PI * 2);
                ctx.fill();

                ctx.drawImage(
                    famImg,
                    famX - famSize / 2,
                    famY - famSize / 2,
                    famSize,
                    famSize
                );
                ctx.restore();
            }
        }
    }

    /** Alias para compatibilidad con la especificación de previsualizador */
    public drawCosmeticPreview(lookAngle: number = 0, t: number = performance.now()): void {
        this.renderStudioPreview(lookAngle, t);
    }

    public updatePreview(): void {
        this.renderStudioPreview();
    }

    public setFamiliar(familiarId: string): void {
        this.currentCosmetics.familiar = familiarId;
        this.saveCosmetics(this.currentCosmetics);
        try {
            localStorage.setItem("alchem_familiar", familiarId);
        } catch {}
        this.renderStudioPreview();
    }

    public setElementSkin(element: string, skinId: string): void {
        if (!this.currentCosmetics.elementSkins) {
            this.currentCosmetics.elementSkins = {
                water: "agua_001",
                fire: "fuego_001",
                plant: "planta_001",
                electric: "electrico_001",
                earth: "tierra_001",
            };
        }
        (this.currentCosmetics.elementSkins as any)[element] = skinId;
        this.saveCosmetics(this.currentCosmetics);
        this.updatePreview();
    }

    private initCosmeticsStudio(): void {
        this.updateStageElementDisplay();

        if (this.stagePrevBtn) {
            this.stagePrevBtn.addEventListener("click", () => {
                this.previewElementIndex = (this.previewElementIndex - 1 + PREVIEW_ELEMENTS.length) % PREVIEW_ELEMENTS.length;
                this.updateStageElementDisplay();
                this.renderStudioPreview();
                const activeTab = document.querySelector(".cosmetics-tab-btn.active") as HTMLElement;
                const activeCategory = (activeTab?.dataset.category as CosmeticCategory) || "eyes";
                if (activeCategory === "aura" || activeCategory === "thruster") {
                    this.renderCategoryOptions(activeCategory);
                }
            });
        }

        if (this.stageNextBtn) {
            this.stageNextBtn.addEventListener("click", () => {
                this.previewElementIndex = (this.previewElementIndex + 1) % PREVIEW_ELEMENTS.length;
                this.updateStageElementDisplay();
                this.renderStudioPreview();
                const activeTab = document.querySelector(".cosmetics-tab-btn.active") as HTMLElement;
                const activeCategory = (activeTab?.dataset.category as CosmeticCategory) || "eyes";
                if (activeCategory === "aura" || activeCategory === "thruster") {
                    this.renderCategoryOptions(activeCategory);
                }
            });
        }

        this.tabButtons.forEach((tab) => {
            tab.addEventListener("click", () => {
                this.tabButtons.forEach((t) => t.classList.remove("active"));
                tab.classList.add("active");
                const activeCategory = (tab.dataset.category as CosmeticCategory) || "eyes";
                this.renderCategoryOptions(activeCategory);
            });
        });

        if (this.stageSaveBtn) {
            this.stageSaveBtn.addEventListener("click", async () => {
                const token = safeGetStorage("alchem_auth_token") || safeGetStorage("colyseus-auth-token");
                if (!token) {
                    await Swal.fire({
                        icon: "info",
                        title: "Inicia Sesión",
                        text: "Crea una cuenta gratis o inicia sesión para guardar tu personalización en la nube de forma permanente.",
                        confirmButtonText: "Iniciar Sesión",
                        confirmButtonColor: "#38bdf8",
                        showCancelButton: true,
                        cancelButtonText: "Seguir jugando",
                        cancelButtonColor: "#64748b",
                    }).then((res) => {
                        if (res.isConfirmed) {
                            const accountBtn = document.getElementById("open-account-btn") || document.getElementById("alias-account-btn");
                            accountBtn?.click();
                        }
                    });
                    return;
                }
                const saveText = this.stageSaveBtn?.querySelector(".save-btn-text");
                const saveIcon = this.stageSaveBtn?.querySelector(".save-btn-icon");
                if (saveText) saveText.textContent = "Guardando...";
                try {
                    await profileManager.syncToDatabase({ cosmetics: this.currentCosmetics });
                    this.saveCosmetics(this.currentCosmetics);
                    if (this.room) {
                        try { this.room.send("set-skin", { skin: JSON.stringify(this.currentCosmetics) }); } catch {}
                    }
                    this.stageSaveBtn?.classList.add("saved");
                    if (saveIcon) saveIcon.textContent = "✓";
                    if (saveText) saveText.textContent = "¡Guardado!";
                    setTimeout(() => {
                        this.stageSaveBtn?.classList.remove("saved");
                        if (saveIcon) saveIcon.textContent = "💾";
                        if (saveText) saveText.textContent = "Guardar";
                    }, 2200);
                } catch (err) {
                    console.error("[Menu] Error al guardar aspecto en base de datos:", err);
                    if (saveText) saveText.textContent = "Error al guardar";
                    setTimeout(() => {
                        if (saveIcon) saveIcon.textContent = "💾";
                        if (saveText) saveText.textContent = "Guardar";
                    }, 2000);
                }
            });
        }

        this.renderCategoryOptions("eyes");
        void this.fetchCatalog();

        // Animación continua del Studio de Cosméticos en el modal de Alias (Escenario Central de 120x120px)
        const animateCosmeticsStudio = (t: number): void => {
            requestAnimationFrame(animateCosmeticsStudio);
            if (this.aliasModal && this.aliasModal.classList.contains("hidden")) {
                return;
            }
            const lookAngle = Math.sin(t * 0.002) * 0.45;
            this.renderStudioPreview(lookAngle, t);
        };
        requestAnimationFrame(animateCosmeticsStudio);
        this.renderStudioPreview();
    }

    private loadSavedCosmetics(): PlayerCosmeticsConfig {
        try {
            const raw = localStorage.getItem(COSMETICS_STORAGE_KEY);
            if (raw) {
                const rawObj = JSON.parse(raw);
                const parsed = sanitizeCosmetics(rawObj);
                if (!rawObj || !("familiar" in rawObj) || !rawObj.familiar) {
                    parsed.familiar = "none";
                }
                return parsed;
            }
        } catch (e) {
            console.warn("[Cosmetics] Error al deserializar cosméticos:", e);
        }
        return { ...DEFAULT_COSMETICS, familiar: "none" };
    }

    private saveCosmetics(cosmetics: PlayerCosmeticsConfig): void {
        try {
            localStorage.setItem(COSMETICS_STORAGE_KEY, JSON.stringify(cosmetics));
            if (cosmetics.familiar) {
                localStorage.setItem("alchem_familiar", cosmetics.familiar);
            }
        } catch (e) {
            console.warn("[Cosmetics] Error al guardar cosméticos:", e);
        }
    }

    /**
     * Sincroniza la cabecera heroica AAA con los balances en tiempo real y el estado de la sesión.
     */
    public updateHeroHeaderState(): void {
        const state = questManager.getState();
        if (this.heroCrystals) {
            this.heroCrystals.textContent = `${state.crystals.toLocaleString()} 💎`;
        }
        if (this.heroOrbs) {
            this.heroOrbs.textContent = `${state.astralOrbs.toLocaleString()} 🔮`;
        }

        const token = safeGetStorage("alchem_auth_token") || safeGetStorage("colyseus-auth-token");
        const isLoggedIn = Boolean(token);

        if (isLoggedIn) {
            if (this.heroLoginBtn) this.heroLoginBtn.classList.add("hidden");
            if (this.heroUserLogged) this.heroUserLogged.classList.remove("hidden");

            const savedAlias = safeGetStorage("alchem_alias");
            const profile = profileManager.getProfile();
            const displayName = savedAlias || (profile as any).alias || "Alquimista";
            if (this.heroUserName) {
                this.heroUserName.textContent = displayName;
            }

            // Consultar disponibilidad del giro gratis para mostrar badge radiante
            fetch("/api/roulette/status", { headers: { Authorization: `Bearer ${token}` } })
                .then((r) => (r.ok ? r.json() : null))
                .then((data) => {
                    const badge = document.getElementById("alias-roulette-badge");
                    if (badge) {
                        badge.classList.toggle("hidden", !data?.canSpinFree);
                    }
                })
                .catch(() => {});
        } else {
            if (this.heroLoginBtn) this.heroLoginBtn.classList.remove("hidden");
            if (this.heroUserLogged) this.heroUserLogged.classList.add("hidden");
            const badge = document.getElementById("alias-roulette-badge");
            if (badge) badge.classList.add("hidden");
        }
    }

    private initHeroHeaderListeners(): void {
        if (this.heroLoginBtn) {
            this.heroLoginBtn.addEventListener("click", () => {
                const accountBtn = document.getElementById("open-account-btn") || document.getElementById("alias-account-btn") || document.getElementById("account-btn");
                if (accountBtn) {
                    accountBtn.click();
                }
            });
        }

        if (this.heroUserInfoBtn) {
            this.heroUserInfoBtn.addEventListener("click", () => {
                window.location.href = "/perfil.html";
            });
        }

        const openRouletteBtn = document.getElementById("open-roulette-btn");
        if (openRouletteBtn) {
            openRouletteBtn.addEventListener("click", () => {
                window.dispatchEvent(new CustomEvent("alchem:open_roulette"));
            });
        }

        if (this.heroLogoutBtn) {
            this.heroLogoutBtn.addEventListener("click", async () => {
                const result = await Swal.fire({
                    title: "¿Cerrar sesión?",
                    text: "¿Deseas cerrar tu sesión actual de Alquimista?",
                    icon: "question",
                    showCancelButton: true,
                    confirmButtonText: "Sí, Salir",
                    cancelButtonText: "Cancelar",
                    confirmButtonColor: "#ef4444",
                    cancelButtonColor: "#64748b",
                });

                if (result.isConfirmed) {
                    clearUserSessionStorage();
                    questManager.resetToGuest();
                    profileManager.resetProfile();
                    localStorage.setItem("alchem_cosmetics", JSON.stringify(DEFAULT_COSMETICS));
                    window.location.reload();
                }
            });
        }
    }

    private initTutorialButton(): void {
        const tutorialBtn = document.getElementById("tutorial-arena-btn");
        if (!tutorialBtn) return;

        tutorialBtn.addEventListener("click", async () => {
            const token = safeGetStorage("alchem_auth_token") || safeGetStorage("colyseus-auth-token");
            if (!token) {
                // Modo Invitado: Advertencia con opción a registrarse
                Swal.fire({
                    title: '⚔️ MODO ENTRENAMIENTO ALQUÍMICO',
                    html: 'Estás jugando como <b>Invitado</b>.<br><br>Si realizas el tutorial sin registrarte <b>NO podrás obtener ni guardar la recompensa de +50 Cristales Alquímicos 💎</b>.<br><br>¿Deseas registrarte ahora para asegurar tu botín de bienvenida?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: '✨ REGISTRARME AHORA',
                    cancelButtonText: '🛡️ PRACTICAR SIN RECOMPENSA',
                    confirmButtonColor: '#38bdf8',
                    cancelButtonColor: '#64748b'
                }).then((res) => {
                    if (res.isConfirmed) {
                        // Abrir modal de cuenta en la pestaña de registro
                        const accountBtn = document.getElementById("open-account-btn") || document.getElementById("alias-account-btn");
                        if (accountBtn) {
                            accountBtn.click();
                            setTimeout(() => {
                                const regTab = (document.getElementById("auth-mode-register-btn") ||
                                    document.getElementById("account-tab-register") ||
                                    document.querySelector('[data-tab="register"]')) as HTMLElement | null;
                                if (regTab) regTab.click();
                            }, 50);
                        }
                    } else if (res.dismiss === Swal.DismissReason.cancel) {
                        // Iniciar tutorial en modo práctica libre
                        startTutorialArena({ isGuest: true, alreadyClaimed: false });
                    }
                });
            } else {
                // Modo Registrado: consultar si ya reclamó la recompensa
                let alreadyClaimed = safeGetStorage("alchem_tutorial_reward_claimed") === "true";
                try {
                    const res = await fetch("/api/profile", {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    if (res.ok) {
                        const p = await res.json();
                        if (p?.questData?.tutorialRewardClaimed) {
                            alreadyClaimed = true;
                            safeSetStorage("alchem_tutorial_reward_claimed", "true");
                        }
                    }
                } catch {
                    // Fallback a localStorage
                }

                if (alreadyClaimed) {
                    Swal.fire({
                        title: '🎓 MODO PRÁCTICA',
                        html: '¡Bienvenido de vuelta!<br><br>Ya has reclamado tu recompensa de bienvenida de +50 💎 anteriormente.<br><br>¡Entrena cuanto quieras para perfeccionar tus técnicas de combate!',
                        icon: 'info',
                        showCancelButton: true,
                        confirmButtonText: '⚔️ ENTRAR A PRACTICAR',
                        cancelButtonText: 'Cancelar',
                        confirmButtonColor: '#38bdf8',
                        cancelButtonColor: '#64748b'
                    }).then((res) => {
                        if (res.isConfirmed) {
                            startTutorialArena({ isGuest: false, alreadyClaimed: true });
                        }
                    });
                } else {
                    Swal.fire({
                        title: '🎓 INICIACIÓN ALQUÍMICA',
                        html: '¡Bienvenido Alquimista!<br><br>Supera este entrenamiento guiado contra el Gólem Elemental y obtén tu recompensa de iniciación:<br><br><b style="color:#fbbf24; font-size:1.2rem;">🎁 +50 Cristales Alquímicos 💎</b><br><small style="color:#94a3b8;">(Válido una sola vez por cuenta)</small>',
                        icon: 'info',
                        showCancelButton: true,
                        confirmButtonText: '⚔️ COMENZAR DESAFÍO',
                        cancelButtonText: 'Más tarde',
                        confirmButtonColor: '#38bdf8',
                        cancelButtonColor: '#64748b'
                    }).then((res) => {
                        if (res.isConfirmed) {
                            startTutorialArena({ isGuest: false, alreadyClaimed: false });
                        }
                    });
                }
            }
        });
    }
}


